import includes from "lodash/includes";
import some from "lodash/some";
import * as React from "react";

import { Unauthorized } from "@app/shared/ErrorPages";
import { UserContext, UserInfo } from "@app/user";
import { DashboardType } from "@app/user/enumerations";
import { Route, RouteProps } from "react-router";
import { JSX } from "react";

/** Defines the set of dashboards to restrict a child component to. */
interface DashboardRestrictedProps {
  /** [Optional] If a user has any of these dashboards they are allowed access */
  allowedDashboards?: DashboardType[];
  /** [Optional] If a user has any of these dashboards they are denied access */
  deniedDashboards?: DashboardType[];
  /** [Optional] Render null instead of unauthorized. `Default: false` */
  renderNull?: boolean;
}

/** Wraps a child component, only rendering when the current user has the correct dashboards. */
class DashboardRestricted extends React.Component<React.PropsWithChildren<DashboardRestrictedProps>> {
  /** Defines the basic case for when no props are provided. */
  public static defaultProps: DashboardRestrictedProps = {
    allowedDashboards: [],
    deniedDashboards: [],
  };

  public render() {
    return (
      <UserContext.Consumer>
        {(user) => canRender(user, this.props.allowedDashboards, this.props.deniedDashboards)
          ? this.props.children
          : this.props.renderNull
            ? null
            : <Unauthorized />
        }
      </UserContext.Consumer>
    );
  }
}

function DashboardRestrictedRoute(props: { allowedDashboards?: DashboardType[], deniedDashboards?: DashboardType[] } & RouteProps): JSX.Element {
  const { allowedDashboards, deniedDashboards, component: Component, ...rest } = props;

  return (
    <Route
      {...rest}
      render={(renderProps) => (
        <UserContext.Consumer>
          {(user) => canRender(user, allowedDashboards || [], deniedDashboards || [])
            ? <Component {...renderProps} />
            : user === undefined
              ? <></>
              : <Unauthorized />
          }
        </UserContext.Consumer>
      )}
    />
  );
}

/**
 * Determines whether the user has the correct dashboards so that the child component can render.
 * If the user has any denied dashboards, then it is an instant fail.
 * @param user The user whose dashboards are checked.
 * @param allowedDashboards
 * @param deniedDashboards
 */
function canRender(user: UserInfo, allowedDashboards?: DashboardType[], deniedDashboards?: DashboardType[]): boolean {
  try {
    if (!user) {
      return false;
    }
    const hasAllowed = some(user.authorizedDashboardTypes, (dashboard) => includes(allowedDashboards, dashboard));
    const hasDenied = some(user.authorizedDashboardTypes, (dashboard) => includes(deniedDashboards, dashboard));

    return !hasDenied && (hasAllowed || allowedDashboards.length === 0);
  } catch (e) {
    return false;
  }
}

export { DashboardRestricted, DashboardRestrictedRoute };
